/**
 * The rating delta counts from 0 over 600 ms WITHOUT a setState per frame: an
 * `Animated.Value` drives a listener that writes straight into the node.
 * `setNativeProps` is the native path; react-native-web 0.19 does not ship it,
 * and there the ref IS the DOM node, so the text is written directly — the
 * component never re-renders, so React cannot fight the write.
 */
import React, {useEffect, useRef} from 'react';
import {Animated, Easing, Text} from 'react-native';

import {TYPE} from '../../theme/tokens';
import {formatDelta} from '../format';

const COUNT_UP_MS = 600;

export default React.memo(function DeltaCounterView({
  delta,
  color,
}: {
  delta: number;
  color: string;
}) {
  const ref = useRef<any>(null);
  const value = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const id = value.addListener(({value: v}) => {
      const node = ref.current;
      if (!node) {
        return;
      }
      const text = formatDelta(v);
      if (typeof node.setNativeProps === 'function') {
        node.setNativeProps({text});
      } else if ('textContent' in node) {
        node.textContent = text;
      }
    });
    value.setValue(0);
    Animated.timing(value, {
      toValue: delta,
      duration: COUNT_UP_MS,
      easing: Easing.bezier(0.2, 0.8, 0.3, 1),
      // the listener needs the JS value, so never the native driver
      useNativeDriver: false,
    }).start();
    return () => value.removeListener(id);
  }, [delta, value]);

  // a raw `Text` and not `ui/Txt`: the ref must be the text node itself, which
  // is what the listener writes into
  return (
    <Text ref={ref} style={[TYPE.delta, styles.numeric, {color}]}>
      {formatDelta(0)}
    </Text>
  );
});

const styles = {
  numeric: {fontVariant: ['tabular-nums'] as any},
};
